/**
 * Calendar-quarter grouping for batches (the Batches screen's Quarterly tab).
 *
 * A batch's period is identified by the offset-local leading date of
 * `period_start` (the PT month — same convention as `isoToGasDay` /
 * `periodLabelFrom`), so quarter derivation is DST-safe and needs no timezone
 * math: the leading `YYYY-MM` is already the Pacific reporting month.
 */
import type { BatchSummary } from "../api/types";

export type Quarter = { year: number; quarter: 1 | 2 | 3 | 4 };

export type QuarterGroup = {
  /** Stable key, e.g. "2025-Q4". */
  key: string;
  year: number;
  quarter: 1 | 2 | 3 | 4;
  /** Display label, e.g. "Q4 2025". */
  label: string;
  batches: BatchSummary[];
};

const PERIOD_RE = /^(\d{4})-(\d{2})/;

/** Year + calendar quarter from a batch's `period_start`, or null if unparseable. */
export function quarterOf(periodStart: string): Quarter | null {
  const m = PERIOD_RE.exec(periodStart);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]); // 1–12
  if (month < 1 || month > 12) return null;
  return { year, quarter: (Math.floor((month - 1) / 3) + 1) as 1 | 2 | 3 | 4 };
}

export function quarterLabel(q: Quarter): string {
  return `Q${q.quarter} ${q.year}`;
}

/**
 * Group batches into calendar quarters. Quarters are returned **most-recent
 * first**; batches within a quarter are sorted **ascending** by `period_start`
 * (Jan → Mar). Batches with an unparseable period are skipped.
 */
export type YearGroup = {
  /** Stable key + label, e.g. "2025". */
  key: string;
  year: number;
  batches: BatchSummary[];
};

/**
 * Group batches into calendar years (the Batches screen's Yearly tab). Years are
 * most-recent first; batches within a year ascend by `period_start`. Batches with
 * an unparseable period are skipped.
 */
export function groupBatchesByYear(batches: BatchSummary[]): YearGroup[] {
  const map = new Map<number, YearGroup>();
  for (const b of batches) {
    const q = quarterOf(b.period_start);
    if (!q) continue;
    let group = map.get(q.year);
    if (!group) {
      group = { key: String(q.year), year: q.year, batches: [] };
      map.set(q.year, group);
    }
    group.batches.push(b);
  }
  const groups = [...map.values()];
  groups.sort((a, b) => b.year - a.year);
  for (const g of groups) {
    // Parsed-instant order, not lexical: mixed UTC offsets can sort lexically
    // != chronologically (BIO-144 #9; mirrors pickActiveUpload/pickActiveBatch).
    g.batches.sort((x, y) => Date.parse(x.period_start) - Date.parse(y.period_start));
  }
  return groups;
}

export function groupBatchesByQuarter(batches: BatchSummary[]): QuarterGroup[] {
  const map = new Map<string, QuarterGroup>();
  for (const b of batches) {
    const q = quarterOf(b.period_start);
    if (!q) continue;
    const key = `${q.year}-Q${q.quarter}`;
    let group = map.get(key);
    if (!group) {
      group = { key, year: q.year, quarter: q.quarter, label: quarterLabel(q), batches: [] };
      map.set(key, group);
    }
    group.batches.push(b);
  }
  const groups = [...map.values()];
  groups.sort((a, b) => b.year - a.year || b.quarter - a.quarter);
  for (const g of groups) {
    // Parsed-instant order, not lexical: mixed UTC offsets can sort lexically
    // != chronologically (BIO-144 #9; mirrors pickActiveUpload/pickActiveBatch).
    g.batches.sort((x, y) => Date.parse(x.period_start) - Date.parse(y.period_start));
  }
  return groups;
}
